import {setAnalyticsConsent} from '@/lib/analytics';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

declare global {
  interface Window {
    loadGoogleAnalytics: () => void;
  }
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    let consent: string | null = null;
    try { consent = localStorage.getItem('cookie-consent'); } catch { /* Storage can be unavailable. */ }
    if (!consent) {
      // Small delay to not interfere with initial page load
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    setAnalyticsConsent(true);
    setShowBanner(false);
  };

  const handleDecline = () => {
    setAnalyticsConsent(false);
    setShowBanner(false);
  };

  return (
    <>
    {!showBanner && <button onClick={()=>setShowBanner(true)} className="fixed bottom-1 right-1 z-[60] rounded bg-white/90 px-2 py-1 text-xs text-gray-700">Cookie settings</button>}
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring" as const, damping: 25, stiffness: 200 }}
          className="print:hidden fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <svg className="w-5 h-5 text-unimo-turquoise" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <h3 className="font-medium text-gray-900">Cookie Preferences</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    We use cookies to analyse site usage and improve your experience.
                    You can accept or decline analytics cookies below.
                  </p>

                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mb-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-2"
                    >
                      <p>
                        <strong>Preference storage:</strong> Remembers your analytics choice on this device so we can respect it.
                      </p>
                      <p>
                        <strong>Analytics cookies:</strong> Help us understand how visitors use our site (Google Analytics).
                        We measure page visits, link clicks, video engagement and demo interactions. We do not send form contents or search text.
                      </p>
                      <p className="text-gray-500">
                        For more information, see our <a href="/privacy" className="text-unimo-turquoise hover:underline">Privacy Policy</a>.
                      </p>
                    </motion.div>
                  )}

                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-xs text-unimo-turquoise hover:underline"
                  >
                    {showDetails ? 'Hide details' : 'Learn more'}
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 md:flex-col lg:flex-row">
                  <button
                    onClick={handleDecline}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Decline
                  </button>
                  <button
                    onClick={handleAccept}
                    className="px-4 py-2 text-sm font-medium text-white bg-unimo-turquoise hover:bg-unimo-turquoise/90 rounded-lg transition-colors"
                  >
                    Accept Analytics
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default CookieConsent;
